import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../../api/axios'
import endpoints from '../../../api/endpoints'
import { extractApiErrorMessage } from '../../../utils/apiError'
import { RequestStatusCode } from '../../../constants/RequestStatus'
import { getStatusLabel } from '../../../utils/requestStatus'

export type CalendarDateOption = 'A' | 'R'

export type CalendarRequestType = 'V' | 'F' | 'D' | 'O' | 'A' | 'B'

export type CalendarRequestStatus = RequestStatusCode

export interface ApproverCalendarFilters {
  dateOption: CalendarDateOption
  startDate?: string
  endDate?: string
  department?: string[]
  union?: string[]
  approvalGroup?: string[]
  jobCode?: string[]
  primaryApprover?: string[]
  status: CalendarRequestStatus[]
  requestType: CalendarRequestType[]
}

export interface ApproverCalendarViewPayload {
  date: string
  selectedDate: string
  filters?: ApproverCalendarFilters
}

export interface ApproverCalendarDayItem {
  date?: string
  hours?: number
  time?: {
    type?: string
    start?: string
    end?: string
    [key: string]: unknown
  }
}

export interface ApproverCalendarApiItem {
  toffReqstId?: string
  empNUID?: string
  empName?: string
  status?: string
  statusCode?: string
  days?: ApproverCalendarDayItem[]
  [key: string]: unknown
}

export interface ApproverCalendarViewResponse {
  weekStart?: string
  weekEnd?: string
  items?: ApproverCalendarApiItem[]
  [key: string]: unknown
}

export interface ApproverCalendarRequestItem {
  rowId: string
  requestId: string
  employeeName: string
  date: string
  status: string
  cellText?: string
}


// =============================
// Partial day time formatter
// =============================

const toMeridiemText = (
  timeValue?: string
): { time: string; suffix: string } => {
  if (!timeValue) return { time: '', suffix: '' }

  const normalized = String(timeValue).trim()

  const meridiemMatch = /(\d{1,2}):?(\d{2})?\s*(AM|PM)/i.exec(
    normalized
  )

  if (meridiemMatch) {
    const hour = Number.parseInt(meridiemMatch[1], 10)
    const minute = meridiemMatch[2] ?? '00'
    const suffix = ` ${meridiemMatch[3].toUpperCase()}`

    return {
      time: `${hour % 12 === 0 ? 12 : hour % 12}.${minute}`,
      suffix,
    }
  }

  const [hourPart, minutePart = '00'] =
    normalized.split(':')

  const hour24 = Number.parseInt(hourPart, 10)
  const minute = Number.parseInt(minutePart, 10)

  if (Number.isNaN(hour24) || Number.isNaN(minute)) {
    return {
      time: normalized,
      suffix: '',
    }
  }

  return {
    time: `${hour24 % 12 === 0 ? 12 : hour24 % 12}.${String(
      minute
    ).padStart(2, '0')}`,
    suffix: hour24 >= 12 ? ' PM' : ' AM',
  }
}


const getPartialCellText = (
  day: ApproverCalendarDayItem
): string | undefined => {
  const timeType = String(day.time?.type ?? '').toUpperCase()

  if (timeType !== 'PARTIAL') return undefined

  const start = toMeridiemText(day.time?.start)
  const end = toMeridiemText(day.time?.end)

  if (!start.time || !end.time) return undefined

  return `${start.time}${start.suffix} - ${end.time}${end.suffix}`
}


const CALENDAR_STATUS_ALIAS_TO_CODE: Record<string, string> = {
  // Submitted group
  FORWARDED: 'SUBMITTED',
  SUBMITTED_RELEASED: 'SUBMITTED',

  // Resubmitted group
  RESUBMITTED_FORWARDED: 'RESUBMITTED',
  RESUBMITTED_RELEASED: 'RESUBMITTED',

  // Approved group
  PAPPROVED: 'APPROVED',
  RTDENIED: 'APPROVED',
  PRTDENIED: 'APPROVED',
  APPROVED_PRTSUBMITTED: 'APPROVED',
  OVERRIDDEN: 'APPROVED',
  PAPPROVED_RTDENIED: 'APPROVED',
  OVERRIDDEN_RTDENIED: 'APPROVED',

  // On Hold group
  ON_HOLD: 'SUBMITTED_ONHOLD',
  ONHOLD: 'SUBMITTED_ONHOLD',
  RESUBMITTED_ONHOLD: 'SUBMITTED_ONHOLD',
}
const CALENDAR_ALLOWED_STATUS_LABELS = new Set([
  'Submitted',
  'Resubmitted',
  'Approved',
  'On Hold',
  'Returned',
])

const toLabel = (status?: string): string => {
  if (!status) return ''

  const normalized = status
    .trim()
    .toUpperCase()
    .replaceAll(/\s+/g, '_')

  const normalizedCode =
    CALENDAR_STATUS_ALIAS_TO_CODE[normalized] ?? normalized

  const label = getStatusLabel(normalizedCode)

  return CALENDAR_ALLOWED_STATUS_LABELS.has(label) ? label : ''
}
// =============================
// Calendar response mapper
// Same behavior as Requestor
// =============================

export const mapApproverCalendarViewResponse = (
  response: ApproverCalendarViewResponse | null | undefined
): ApproverCalendarRequestItem[] => {
  if (!response?.items) return []

  const rows: ApproverCalendarRequestItem[] = []

  response.items.forEach((item, index) => {
    const employeeName = item.empName ?? ''

    if (!employeeName) return

const mappedStatus = toLabel(item.statusCode ?? item.status)

if (!mappedStatus) {
  return
}
    item.days?.forEach((day) => {
      if (!day.date) return

      rows.push({
        rowId:
          item.toffReqstId ??
          `${employeeName}-${day.date}-${index}`,
        requestId: item.toffReqstId ?? '',
        employeeName,

        date: day.date,

        status: mappedStatus,

        cellText: getPartialCellText(day),
      })
    })
  })

  return rows
}


// =============================
// Default filters
// =============================

export const DEFAULT_APPROVER_STATUS_CODES: CalendarRequestStatus[] = [
  RequestStatusCode.SUBMITTED,
  RequestStatusCode.RTSUBMITTED,
  RequestStatusCode.RESUBMITTED,
]


export interface ApproverCalendarPayload {
  date: string
  selectedDate: string
  filters: ApproverCalendarFilters
}


export const DEFAULT_APPROVER_REQUEST_TYPES: CalendarRequestType[] = [
  'V',
  'F',
  'D',
  'O',
  'A',
  'B',
]


// =============================
// API thunk
// =============================

export const getApproverCalendarView = createAsyncThunk(
  'approverCalendarView/getCalendarView',

  async (
    payload: ApproverCalendarViewPayload,
    { rejectWithValue, signal }
  ) => {
    try {
      const response = await api.post(
        endpoints.approver.calendarView,
        payload,
        {
          signal,
        }
      )

      return response.data as ApproverCalendarViewResponse

    } catch (error: any) {

      return rejectWithValue(
        extractApiErrorMessage(
          error,
          'Failed to fetch approver calendar view'
        )
      )
    }
  }
)


// =============================
// Redux State
// =============================

export interface ApproverCalendarViewState {
  data: ApproverCalendarViewResponse | null
  loading: boolean
  error: string | null
  totalRecords:number
}


const initialState: ApproverCalendarViewState = {
  data: null,
  loading: false,
  error: null,
  totalRecords: 0,
}


const slice = createSlice({
  name: 'approverCalendarView',

  initialState,

  reducers: {

    clearApproverCalendarView: (state) => {
      state.data = null
      state.error = null
      state.loading = false
    },

  },


  extraReducers: (builder) => {

    builder

      .addCase(
        getApproverCalendarView.pending,
        (state) => {
          state.loading = true
          state.error = null
        }
      )


      .addCase(
        getApproverCalendarView.fulfilled,
        (state, action) => {
          state.loading = false
          state.data = action.payload
          state.totalRecords = action.payload.items?.length ?? 0
          state.error = null
        }
      )


      .addCase(
        getApproverCalendarView.rejected,
        (state, action) => {
          state.loading = false
          state.error = action.payload as string
        }
      )
  },
})


export const {
  clearApproverCalendarView,
} = slice.actions


export default slice.reducer